import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { EntityType } from "@mapmycustomers/shared";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { Deal } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import localSettings from "@app/config/LocalSettings";
import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import {
  applyClosedStageDealAmountDrillDownListViewSettings,
  exportClosedStageDealAmountCardDrillDownData,
  fetchClosedStageDealAmountCardData,
  fetchClosedStageDealAmountCardDrillDownData,
  showClosedStageDealAmountDrillDown,
} from "@app/scene/dashboard/store/cards/closedStageDealAmount/actions";
import {
  getClosedStageDealAmountDrillDownTotalFilteredRecords,
  getClosedStageDealAmountDrillDownViewState,
} from "@app/scene/dashboard/store/cards/closedStageDealAmount/selectors";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import expandColumns from "@app/scene/dashboard/utils/expandColumns";
import { callApi } from "@app/store/api/callApi";
import { fetchClosedStageDealAmountCardDataHelper } from "@app/store/dashboard/cardDataFetchHelpers";
import { handleError } from "@app/store/errors/actions";
import { exportEntities } from "@app/store/exportEntities/actions";
import { getOrganizationId } from "@app/store/iam";
import dealFieldModel, { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import {
  createAndCondition,
  createEmptyFilterModel,
  createSimpleCondition,
} from "@app/util/filters/utils";
import loggingService from "@app/util/logging";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import getDefaultDrillDownColumns from "../../utils/getDefaultDrillDownColumns";

const defaultColumns: string[] = [
  DealFieldName.NAME,
  DealFieldName.USER,
  DealFieldName.ADDRESS,
  DealFieldName.AMOUNT,
  DealFieldName.CREATED_AT,
  DealFieldName.SOURCE,
];

const defaultSortColumn = DealFieldName.CREATED_AT;

function* onFetchClosedStageDealAmountCardData({
  payload,
}: ReturnType<typeof fetchClosedStageDealAmountCardData>) {
  const { callback, configuration, failureCallback, scope } = payload;

  try {
    yield call(fetchClosedStageDealAmountCardDataHelper, { callback, configuration, scope });
  } catch (error) {
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

function* onOpenDrillDown({
  payload,
}: ReturnType<typeof showClosedStageDealAmountDrillDown.request>) {
  const { configuration, customDateRange, scope } = payload;
  const { criteria, dateRange } = configuration;

  const drillDownViewState: DrillDownViewState = yield select(
    getClosedStageDealAmountDrillDownViewState
  );

  const viewState = localSettings.getViewSettings(
    `dashboard/closedStageDealAmount`,
    dealFieldModel,
    {
      ...drillDownViewState,
      columns: getDefaultDrillDownColumns(defaultColumns, dealFieldModel),
      sort: [
        {
          field: dealFieldModel.getByName(defaultSortColumn)!,
          order: SortOrder.DESC,
        },
      ],
    }
  );

  const isTeamScope = !!scope?.teamId && !scope?.userId;
  const range = customDateRange ?? getDatesRange(dateRange.range, dateRange.subRange);

  const filter: FilterModel = createAndCondition([
    createSimpleCondition(DealFieldName.CLOSING_DATE, FilterOperator.IN_RANGE, [
      range.startDate,
      range.endDate,
    ]),
  ]);
  if (criteria.dealStageIds.length) {
    filter.conditions.push(
      createSimpleCondition(DealFieldName.STAGE, FilterOperator.IN_ANY, criteria.dealStageIds)
    );
  }
  if (isTeamScope) {
    filter.conditions.push(
      createSimpleCondition(DealFieldName.TEAM, FilterOperator.IN_ANY, [scope.teamId!])
    );
  }
  if (scope?.userId) {
    filter.conditions.push(
      createSimpleCondition(DealFieldName.USER, FilterOperator.IN_ANY, [scope.userId])
    );
  }
  viewState.filter = filter;

  yield put(showClosedStageDealAmountDrillDown.success({ viewState }));
}

export function* onFetchDrillDownData({
  payload,
}: ReturnType<typeof fetchClosedStageDealAmountCardDrillDownData.request>) {
  try {
    loggingService.debug("Dashboard: Deal Count card, onFetchDrillDownData", payload);
    if (!payload.updateOnly) {
      // We do not listen to filter returned by AgGrid from PlatformDataSource
      delete payload.request.filter;
    }

    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applyClosedStageDealAmountDrillDownListViewSettings(payload.request));
    }

    const drillDownViewState: DrillDownViewState = yield select(
      getClosedStageDealAmountDrillDownViewState
    );

    if (!payload.fetchOnlyWithoutFilters) {
      localSettings.setViewSettings(drillDownViewState, `dashboard/closedStageDealAmount`);
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.startRow
        : drillDownViewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.endRow - payload.request.range.startRow
        : drillDownViewState.range.endRow - drillDownViewState.range.startRow;

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const requestPayload = {
      $filters: {
        includeAccessStatus: true,
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : drillDownViewState.filter,
          drillDownViewState.columns,
          dealFieldModel
        ),
      },
      $limit,
      $offset,
      $order: convertToPlatformSortModel(drillDownViewState.sort),
    };

    const response: ListResponse<Deal> = yield callApi("fetchDeals", orgId, requestPayload);
    payload.dataCallback?.(response);
    yield put(
      fetchClosedStageDealAmountCardDrillDownData.success({
        totalFilteredRecords: response.total,
        totalRecords: response.accessible,
      })
    );
  } catch (error) {
    payload.failCallback?.();
    yield put(fetchClosedStageDealAmountCardDrillDownData.failure(error));
    yield put(handleError({ error }));
  }
}

function* onExport() {
  const drillDownViewState: DrillDownViewState = yield select(
    getClosedStageDealAmountDrillDownViewState
  );
  const total: number = yield select(getClosedStageDealAmountDrillDownTotalFilteredRecords);

  yield put(
    exportEntities.request({
      entityType: EntityType.DEAL,
      total,
      viewState: expandColumns(drillDownViewState),
    })
  );
}

export function* closedStageDealAmountSagas() {
  // we use takeEvery because there might be several cards of such type on board
  // however, it would be more optimal to use takeLatest, but also filter by card ids
  yield takeEvery(fetchClosedStageDealAmountCardData, onFetchClosedStageDealAmountCardData);
  yield takeLatest(showClosedStageDealAmountDrillDown.request, onOpenDrillDown);
  yield takeLatest(fetchClosedStageDealAmountCardDrillDownData.request, onFetchDrillDownData);
  yield takeLatest(exportClosedStageDealAmountCardDrillDownData, onExport);
}
